/**
 * IDT KPI ページの期間 (月 / 四半期) 選択ユーティリティ。
 *
 * 履歴回溯ドロップダウン用に「選択可能な月 / Q」の一覧と、Q のラベル / 構成月を提供する。
 * Q のグルーピングは API (`team-target-overrides.quarterMonthsOf`) と同一 (4 月始まりの
 * 3 ヶ月束)。ラベルだけ運用慣習 (7 月始まり会計年度: 1-3 月=3Q / 4-6 月=4Q / 7-9 月=1Q /
 * 10-12 月=2Q) に合わせる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI 履歴回溯の期間 (月/Q) 一覧・ラベル・構成月の解決
 * @graph-connects none
 */

/** データが存在する最古の月 (2026_1Q = 2025 年 7 月始まり。1Q/2Q/3Q は静的履歴) @graph-connects none */
export const IDT_EARLIEST_MONTH = '2025-07';

/** JST 当月を "YYYY-MM" で返す @graph-connects none */
export function jstCurrentMonth(): string {
  const now = new Date();
  const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return jst.toISOString().slice(0, 7);
}

/**
 * "YYYY-MM" → その月が属する Q の 3 ヶ月配列 (API と同じ 4 月始まりグルーピング)。
 * @graph-connects none
 */
export function quarterMonthsOf(month: string): string[] {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return [month];
  const year = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const offset = ((mo - 4 + 12) % 12) % 3;
  const startMonth = mo - offset;
  return [0, 1, 2].map((i) => `${String(year)}-${String(startMonth + i).padStart(2, '0')}`);
}

/**
 * "YYYY-MM" → 運用慣習の Q ラベル (例: "2026 4Q")。
 * 7 月始まり会計年度: 1-3 月=3Q / 4-6 月=4Q / 7-9 月=1Q / 10-12 月=2Q。
 * 会計年度の年表記は 1-6 月の暦年に合わせる (7-12 月は翌暦年が会計年度)。
 * @graph-connects none
 */
export function quarterLabelOf(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const year = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  let q: number;
  let fyYear: number;
  if (mo <= 3) {
    q = 3;
    fyYear = year;
  } else if (mo <= 6) {
    q = 4;
    fyYear = year;
  } else if (mo <= 9) {
    q = 1;
    fyYear = year + 1;
  } else {
    q = 2;
    fyYear = year + 1;
  }
  return `${String(fyYear)} ${String(q)}Q`;
}

/** "YYYY-MM" → "YYYY年M月" @graph-connects none */
export function monthLabelOf(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  return `${m[1]}年${String(parseInt(m[2], 10))}月`;
}

/** "YYYY-MM" を 1 つ後の月へ @graph-connects none */
export function nextMonth(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  let year = parseInt(m[1], 10);
  let mo = parseInt(m[2], 10) + 1;
  if (mo > 12) {
    mo = 1;
    year += 1;
  }
  return `${String(year)}-${String(mo).padStart(2, '0')}`;
}

/** "YYYY-MM" を 1 つ前の月へ @graph-connects none */
export function prevMonth(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  let year = parseInt(m[1], 10);
  let mo = parseInt(m[2], 10) - 1;
  if (mo < 1) {
    mo = 12;
    year -= 1;
  }
  return `${String(year)}-${String(mo).padStart(2, '0')}`;
}

/**
 * 当月から最古月まで降順の月一覧 (月 view ドロップダウン用)。
 * @graph-connects none
 */
export function availableMonths(currentMonth = jstCurrentMonth()): string[] {
  const months: string[] = [];
  let m = currentMonth;
  while (m >= IDT_EARLIEST_MONTH) {
    months.push(m);
    if (m === IDT_EARLIEST_MONTH) break;
    m = prevMonth(m);
  }
  return months;
}

export interface QuarterOption {
  /** Q の代表月 (構成月の末尾)。API 呼び出し / quarterMonthsOf に使う */
  representativeMonth: string;
  /** 表示ラベル (例: "2026 4Q") */
  label: string;
  /** 構成 3 ヶ月 */
  months: string[];
}

/**
 * 選択可能な Q 一覧 (新しい順)。availableMonths が属する Q を distinct 化する。
 * @graph-connects none
 */
export function availableQuarters(currentMonth = jstCurrentMonth()): QuarterOption[] {
  const seen = new Set<string>();
  const quarters: QuarterOption[] = [];
  for (const m of availableMonths(currentMonth)) {
    const months = quarterMonthsOf(m);
    const key = months[0];
    if (!key || seen.has(key)) continue;
    seen.add(key);
    quarters.push({
      representativeMonth: months[months.length - 1] ?? m,
      label: quarterLabelOf(m),
      months,
    });
  }
  return quarters;
}
